import React, { useRef, useEffect, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import { motion, AnimatePresence } from 'framer-motion';
import HoneycombSphere from './HoneycombSphere';
import MagicBento from './MagicBento';

gsap.registerPlugin(ScrollTrigger, TextPlugin);

const T = {
  ink: '#0A0A0A',
  inkSoft: '#52525B',
  inkMute: '#A1A1AA',
  hair: '#E4E4E7',
  cream: '#F7F6F2',
  white: '#FFFFFF',
  violet: '#7C3AED',
  violetSoft: '#A78BFA',
  violetDark: '#5B21B6',
};

const SERIF = '"Nasalization", Georgia, serif';
const SANS = '"Inter", system-ui, sans-serif';
const MONO = '"JetBrains Mono", monospace';

/* ─────────────────────────────────────────────────────────────────
 * Visual Blocks
 * ───────────────────────────────────────────────────────────────── */

const hash = '0x9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08';



/* ─── The Proof Sphere (Abstract 3D Glass) ─── */

const VolumetricRing = ({ size, ringWidth, depth, animateProps, duration, reverse, isSealed }) => {
  const layers = Array.from({ length: depth });
  const r = depth / 2;
  return (
    <motion.div
      animate={animateProps}
      transition={{ duration, repeat: Infinity, ease: 'linear', direction: reverse ? 'reverse' : 'normal' }}
      style={{
        position: 'absolute', top: '50%', left: '50%', x: '-50%', y: '-50%',
        width: `${size}px`, height: `${size}px`,
        transformStyle: 'preserve-3d',
      }}
    >
      {layers.map((_, i) => {
        // True Torus Math to curve the edges
        const dz = Math.abs(i - r + 0.5);
        const ratio = Math.sqrt(Math.max(0, r * r - dz * dz)) / r;

        const currentSize = size - ringWidth * (1 - ratio);
        const currentRingWidth = Math.max(0.5, ringWidth * ratio);
        const radius = (currentSize - currentRingWidth) / 2;

        // Simulating Lambertian shading for a highly polished 3D surface
        const l = (220 + 35 * ratio) / 255;

        // Ceramic white gradient mesh colors with shading applied
        const color1 = `rgba(${Math.floor(255 * l)}, ${Math.floor(255 * l)}, ${Math.floor(255 * l)}, 0.95)`;
        const color2 = `rgba(${Math.floor(226 * l)}, ${Math.floor(232 * l)}, ${Math.floor(240 * l)}, 0.95)`;
        const color3 = `rgba(${Math.floor(248 * l)}, ${Math.floor(250 * l)}, ${Math.floor(252 * l)}, 0.95)`;

        const isFace = i === 0 || i === depth - 1;
        const sealedGlow = isSealed ? 'rgba(16,185,129,0.8)' : 'rgba(236,72,153,0.5)';

        return (
          <svg
            key={i}
            style={{
              position: 'absolute',
              top: '50%', left: '50%',
              width: `${currentSize}px`, height: `${currentSize}px`,
              transform: `translate(-50%, -50%) translateZ(${i - r + 0.5}px)`,
              overflow: 'visible',
              filter: isFace ? `drop-shadow(0px 0px 15px ${sealedGlow})` : 'none',
            }}
            viewBox={`0 0 ${currentSize} ${currentSize}`}
          >
            <defs>
              <linearGradient id={`grad-${size}-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={color1} />
                <stop offset="50%" stopColor={color2} />
                <stop offset="100%" stopColor={color3} />
              </linearGradient>
            </defs>
            <circle
              cx={currentSize / 2}
              cy={currentSize / 2}
              r={radius}
              fill="none"
              stroke={`url(#grad-${size}-${i})`}
              strokeWidth={currentRingWidth}
            />
          </svg>
        );
      })}
    </motion.div>
  );
};

const ProvenanceVisual = () => {
  const [phase, setPhase] = useState(0); // 0=idle, 1=hashing, 2=sealed

  useEffect(() => {
    const sequence = [{ next: 1, delay: 2000 }, { next: 2, delay: 5000 }, { next: 0, delay: 8000 }];
    const timer = setTimeout(() => setPhase(sequence[phase].next), sequence[phase].delay);
    return () => clearTimeout(timer);
  }, [phase]);

  const isHashing = phase === 1;
  const isSealed = phase === 2;

  // The base glow tint that changes with the cryptographic phase
  const glowColor = isSealed ? 'rgba(16, 185, 129, 0.4)' : isHashing ? 'rgba(124, 58, 237, 0.5)' : 'rgba(200, 220, 255, 0.5)';
  const solidGlowColor = isSealed ? 'rgba(16, 185, 129, 0.8)' : isHashing ? 'rgba(124, 58, 237, 0.8)' : 'rgba(200, 220, 255, 0.8)';

  return (
    <div style={{ width: '100%', height: '800px', display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: '2000px' }}>

      {/* 3D Container - Scaled up 2x */}
      <div style={{
        position: 'relative', width: '600px', height: '600px',
        transformStyle: 'preserve-3d',
      }}>
        {/* Core Sphere (True 3D R3F Hollow Buckyball) */}
        <motion.div
          style={{
            position: 'absolute', top: '50%', left: '50%', x: '-50%', y: '-50%',
            width: '460px', height: '460px',
            transformStyle: 'preserve-3d'
          }}
        >
          <HoneycombSphere phase={phase} />
        </motion.div>

        {/* Ring 1 (Inner) - Volumetric 3D */}
        <VolumetricRing
          size={440} ringWidth={24} depth={12}
          animateProps={{ rotateX: [20, 380], rotateY: [40, 400], rotateZ: [10, 370] }}
          duration={isHashing ? 2 : 6} isSealed={isSealed}
        />

        {/* Ring 2 (Middle) - Volumetric 3D */}
        <VolumetricRing
          size={560} ringWidth={24} depth={12}
          animateProps={{ rotateX: [70, 430], rotateY: [10, 370], rotateZ: [50, 410] }}
          duration={isHashing ? 3 : 8} reverse isSealed={isSealed}
        />

        {/* Ring 3 (Outer) - Volumetric 3D */}
        <VolumetricRing
          size={680} ringWidth={24} depth={12}
          animateProps={{ rotateX: [120, 480], rotateY: [80, 440], rotateZ: [90, 450] }}
          duration={isHashing ? 4 : 10} isSealed={isSealed}
        />
      </div>


    </div>
  );
};











/* ─────────────────────────────────────────────────────────────────
 * The Main Component
 * ───────────────────────────────────────────────────────────────── */
export function HeroStory({ onSignInClick }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Section refs
  const sectionRef = useRef(null);
  const introTextRef = useRef(null);

  // Text refs
  const text1Ref = useRef(null);
  const text2Ref = useRef(null);
  const text3Ref = useRef(null);

  // Visual containers
  const gpuVisRef = useRef(null);
  const provVisRef = useRef(null);
  const apiVisRef = useRef(null);

  // Canvas Sequence Player
  const canvasRef = useRef(null);
  const totalFrames = 302;
  const imagesRef = useRef([]);

  useEffect(() => {
    // Preload all frames
    for (let i = 1; i <= totalFrames; i++) {
      const img = new Image();
      const paddedIndex = i.toString().padStart(3, '0');
      img.src = `/gpus/blast_frames_alpha/frame_${paddedIndex}.png`;
      imagesRef.current.push(img);
    }

    // Draw first frame when loaded
    if (imagesRef.current[0]) {
      imagesRef.current[0].onload = () => {
        if (canvasRef.current && imagesRef.current[0].naturalWidth > 0) {
          const ctx = canvasRef.current.getContext('2d');
          try { ctx.drawImage(imagesRef.current[0], 0, 0, 1280, 720); } catch (e) { }
        }
      };
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) return; // Disable complex pinning on mobile for better UX

    let ctx = gsap.context(() => {
      // Initial states
      gsap.set([text1Ref.current, text2Ref.current, text3Ref.current], { autoAlpha: 0, y: 40 });
      gsap.set([provVisRef.current, apiVisRef.current], { autoAlpha: 0, scale: 0.95 });
      gsap.set(gpuVisRef.current, { autoAlpha: 0, scale: 0.9 });
      
      // Clear all typewriter bodies initially
      const typeWriters = document.querySelectorAll('.typewriter-target');
      typeWriters.forEach(tw => { tw.innerHTML = ''; });

      // Initial Sequence scrub object
      const seqObj = { frame: 0 };

      // Initial canvas draw
      if (canvasRef.current && imagesRef.current[0] && imagesRef.current[0].complete && imagesRef.current[0].naturalWidth > 0) {
        const ctx = canvasRef.current.getContext('2d');
        try { ctx.drawImage(imagesRef.current[0], 0, 0, 1280, 720); } catch (e) { }
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "+=400%", // 400vh of scrolling
          scrub: 1,
          pin: true,
          anticipatePin: 1
        }
      });

      // 1. Intro fades out (Sequential)
      tl.to(introTextRef.current, { autoAlpha: 0, y: -40, duration: 0.5 })

        // Pillar 1 & GPU fade in
        .to(text1Ref.current, { autoAlpha: 1, y: 0, duration: 0.5 })
        .to(gpuVisRef.current, { autoAlpha: 1, scale: 1, duration: 0.5 }, "<")
        .to(text1Ref.current.querySelector('.typewriter-target'), { 
          text: text1Ref.current.querySelector('.typewriter-target').getAttribute('data-text'), 
          duration: 1.5, ease: "none" 
        }, "<0.2")

        // The explosion effect - scrub canvas sequence
        .to(seqObj, {
          frame: totalFrames - 1,
          snap: "frame",
          duration: 2,
          ease: "none",
          onUpdate: () => {
            if (canvasRef.current && imagesRef.current[seqObj.frame]) {
              const img = imagesRef.current[seqObj.frame];
              if (img.complete && img.naturalWidth > 0) {
                const ctx = canvasRef.current.getContext('2d');
                ctx.clearRect(0, 0, 1280, 720);
                try { ctx.drawImage(img, 0, 0, 1280, 720); } catch (e) { }
              }
            }
          }
        }, "<") // Start explosion alongside fade in for fluidity
        // Pause to read
        .to({}, { duration: 1.5 })

        // 2. Pillar 1 out, Pillar 2 in (Sequential)
        .to(text1Ref.current, { autoAlpha: 0, y: -40, duration: 0.5 })
        .to(gpuVisRef.current, { autoAlpha: 0, scale: 1.1, duration: 0.5 }, "<")

        .to(text2Ref.current, { autoAlpha: 1, y: 0, duration: 0.5 })
        .to(provVisRef.current, { autoAlpha: 1, scale: 1, duration: 0.5 }, "<")
        .to(text2Ref.current.querySelector('.typewriter-target'), { 
          text: text2Ref.current.querySelector('.typewriter-target').getAttribute('data-text'), 
          duration: 1.5, ease: "none" 
        }, "<0.2")
        // Pause to read
        .to({}, { duration: 1.5 })

        // 3. Pillar 2 out, Pillar 3 in (Sequential)
        .to(text2Ref.current, { autoAlpha: 0, y: -40, duration: 0.5 })
        .to(provVisRef.current, { autoAlpha: 0, scale: 1.05, duration: 0.5 }, "<")

        .to(text3Ref.current, { autoAlpha: 1, y: 0, duration: 0.5 })
        .to(apiVisRef.current, { autoAlpha: 1, scale: 1, duration: 0.5 }, "<")
        .to(text3Ref.current.querySelector('.typewriter-target'), { 
          text: text3Ref.current.querySelector('.typewriter-target').getAttribute('data-text'), 
          duration: 1.5, ease: "none" 
        }, "<0.2")
        // Final pause
        .to({}, { duration: 1.5 });

    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile]);

  const TextBlock = ({ title, body, refObj, align = 'left' }) => (
    <div ref={refObj} style={{ 
      position: isMobile ? 'relative' : 'absolute', 
      width: isMobile ? '100%' : '32%', 
      left: !isMobile && align === 'left' ? '0' : 'auto', // Shifted left to use the pure left breathing space
      right: !isMobile && align === 'right' ? '0' : 'auto',
      marginBottom: isMobile ? '40px' : 0 
    }}>
      <h3 style={{ fontFamily: SERIF, fontSize: 'clamp(2.5rem, 4vw, 4.5rem)', lineHeight: 1.05, letterSpacing: '-0.03em', color: T.white, marginBottom: '28px', fontWeight: 300, fontVariationSettings: '"opsz" 144, "SOFT" 30', whiteSpace: 'nowrap' }}>
        {title}
      </h3>
      <div style={{ position: 'relative' }}>
        {/* Invisible source to maintain layout height and provide text content */}
        <p className="typewriter-source" style={{ fontFamily: SANS, fontSize: 'clamp(16px, 1.3vw, 20px)', lineHeight: 1.6, color: 'rgba(255,255,255,0)', fontWeight: 400, maxWidth: '540px', pointerEvents: 'none' }}>
          {body}
        </p>
        {/* Visible animated target */}
        <p className="typewriter-target" data-text={body} style={{ position: 'absolute', top: 0, left: 0, fontFamily: SANS, fontSize: 'clamp(16px, 1.3vw, 20px)', lineHeight: 1.6, color: 'rgba(255,255,255,0.7)', fontWeight: 400, maxWidth: '540px' }}></p>
      </div>
    </div>
  );
  return (
    <div style={{ position: 'relative' }}>
      <section
        ref={sectionRef}
        style={{
          position: 'relative',
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          background: 'var(--background-dark)'
        }}
      >
        <div style={{
          maxWidth: '1440px', margin: '0 auto', height: '100%',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center', position: 'relative'
        }}>
          {/* Intro Text (Left) */}
          <div ref={introTextRef} style={{
            position: 'absolute',
            left: isMobile ? '5%' : '5%',
            top: '50%',
            transform: 'translateY(-50%)',
            width: isMobile ? '90%' : '30%', // Tightened to 30% to maximize Bento grid width
            zIndex: 20,
            pointerEvents: 'none'
          }}>
            <h1 style={{ fontFamily: SERIF, fontWeight: 300, fontSize: 'clamp(3.5rem, 6vw, 6.5rem)', lineHeight: 0.95, letterSpacing: '-0.04em', color: T.white, marginBottom: '24px', fontVariationSettings: '"opsz" 144, "SOFT" 30' }}>
              The AI Economy, <br />
              <em style={{ fontStyle: 'italic', fontWeight: 400, background: `linear-gradient(135deg, ${T.violetSoft}, ${T.violet})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block', paddingRight: '0.12em', paddingBottom: '0.08em' }}>Redefined.</em>
            </h1>
            <p style={{ fontFamily: SANS, fontSize: 'clamp(16px, 1.2vw, 20px)', lineHeight: 1.6, color: 'rgba(255,255,255,0.6)', maxWidth: '540px', fontWeight: 400, marginBottom: '32px' }}>
              Don't just consume AI. Power it, own your outputs, and build with unrestricted freedom. Welcome to the world's first decentralized intelligence ecosystem.
            </p>
            <motion.button
              onClick={onSignInClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                pointerEvents: 'auto',
                padding: '14px 32px',
                background: `linear-gradient(135deg, ${T.violetSoft}, ${T.violet})`,
                color: T.white,
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '999px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: `0 10px 24px rgba(124, 58, 237, 0.4)`,
              }}
            >
              Sign In
            </motion.button>
          </div>

          {/* Typography Blocks (Alternating Layout) */}
          <TextBlock
            refObj={text1Ref} align="left" title={<>Produce your <br/> own Credits.</>}
            body="Right now, developers are forced to buy subscriptions across a dozen scattered AI platforms. DAGGPT flips the model. By hosting a GPU on our network, you produce your own AI credits - granting you unrestricted access to our entire ecosystem of 400+ frontier models without paying a cent to centralized providers."
          />
          <TextBlock
            refObj={text2Ref} align="right" title={<>Own your <br/> creations <br/> Forever.</>}
            body="When you generate content, code, or art, you should own the IP. DAGGPT is natively integrated with DAGCHAIN, an AI-first blockchain. Every piece of content you create is cryptographically hashed, timestamped, and immutably anchored to the ledger, proving your original ownership and preserving your IP rights indefinitely."
          />
          <TextBlock
            refObj={text3Ref} align="left" title={<>One API. <br/> 400+ <br/> Models.</>}
            body="Developers shouldn't have to juggle API keys, varying documentation, and complex billing from OpenAI, Anthropic, Google, and Meta. DAGGPT gives you a single, unified API key. Access text, image, video, music, and code generation across every major open and closed-source model through one seamless interface."
          />

          {/* Visual 1: Exploded GPU Canvas (Right) */}
          <div ref={gpuVisRef} style={{
            position: isMobile ? 'relative' : 'absolute',
            width: isMobile ? '100%' : '55%',
            right: 0,
            height: isMobile ? '500px' : '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 5, transform: isMobile ? 'none' : 'translateX(5%)'
          }}>
            <div style={{ position: 'relative', width: '100%', maxWidth: '1200px', aspectRatio: '16/9' }}>
              <canvas
                ref={canvasRef}
                width={1280}
                height={720}
                style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 40px 50px rgba(124,58,237,0.3))' }}
              />
            </div>
          </div>

          {/* Visual 2: Provenance (Left) */}
          <div ref={provVisRef} style={{
            position: isMobile ? 'relative' : 'absolute',
            left: isMobile ? 0 : '60px',
            width: isMobile ? '100%' : '50%',
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center', justifyContent: 'flex-start',
            zIndex: 10
          }}>
            <ProvenanceVisual />
          </div>

          {/* Visual 3: Magic Bento API (Right) */}
          <div ref={apiVisRef} style={{
            position: isMobile ? 'relative' : 'absolute',
            right: isMobile ? 0 : '2%',
            width: isMobile ? '100%' : '65%', // Ultra-wide 65% container guarantees true landscape ratios for the 50% width cards
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center', justifyContent: 'flex-end',
            zIndex: 10
          }}>
            <MagicBento
              textAutoHide={false}
              enableStars
              enableSpotlight
              enableBorderGlow={true}
              enableTilt={false}
              enableMagnetism={false}
              clickEffect
              spotlightRadius={400}
              particleCount={12}
              glowColor="132, 0, 255"
              disableAnimations={false}
            />
          </div>

        </div>
      </section>
    </div>
  );
}
